import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShowcaseProject } from '@/types/partner';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

interface Props {
  projects: ShowcaseProject[];
}

const ProjectTile: React.FC<{
  project: ShowcaseProject;
  className?: string;
}> = ({ project, className }) => {
  const { tl } = useLanguage();
  const image = project.images?.[0] || '/placeholder.svg';

  return (
    <Link
      to={`/partners/${project.partner_id}?project=${project.id}`}
      className={cn('group relative block overflow-hidden bg-neutral-200', className)}
    >
      <img
        src={image}
        alt={project.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/35 transition-colors duration-500 group-hover:bg-black/45" />

      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <h3 className="max-w-xl text-lg font-semibold uppercase tracking-[0.14em] text-white md:text-xl lg:text-2xl">
          {project.title}
        </h3>
        <span className="mt-6 text-[11px] font-medium uppercase tracking-[0.2em] text-white underline underline-offset-8 decoration-white/80 transition-opacity group-hover:opacity-80">
          {tl('Découvrir', 'Discover', 'Scopri')}
        </span>
      </div>
    </Link>
  );
};

const ProjectsShowcaseSection: React.FC<Props> = ({ projects }) => {
  const slots = useMemo(() => {
    const map: Partial<Record<1 | 2 | 3, ShowcaseProject>> = {};
    projects.forEach((project) => {
      const pos = Number(project.showcase_position);
      if (pos === 1 || pos === 2 || pos === 3) {
        map[pos as 1 | 2 | 3] = project;
      }
    });
    // Fallback for legacy rows without position: fill empty slots in order
    if (!map[1] && !map[2] && !map[3] && projects.length) {
      projects.slice(0, 3).forEach((project, index) => {
        map[(index + 1) as 1 | 2 | 3] = project;
      });
    }
    return map;
  }, [projects]);

  if (!slots[1] && !slots[2] && !slots[3]) return null;

  return (
    <section className="bg-white px-3 py-10 md:px-5 md:py-14 lg:px-6">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        {slots[1] && <ProjectTile project={slots[1]} className="aspect-square w-full" />}
        {slots[2] && <ProjectTile project={slots[2]} className="aspect-square w-full" />}
        {slots[3] && (
          <ProjectTile
            project={slots[3]}
            className="aspect-[16/9] w-full md:col-span-2 md:aspect-[2.4/1] md:min-h-[320px]"
          />
        )}
      </div>
    </section>
  );
};

export default ProjectsShowcaseSection;
